import { faUsers } from '@fortawesome/pro-solid-svg-icons/faUsers';
import useNonInitialEffect from '@versiondos/hooks';
import { fetchStaffThunk } from 'actions/staff/StaffActions';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import TagsSelector from 'components/UI/V3/TagsSelector';
import isEmpty from 'lodash/isEmpty';
import Staff from 'model/Staff';
import { ChangeEvent, FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StaffStatus } from 'reducers/staff/StaffState';
import { RootState } from 'store';
import { Room, RoomPhoto } from '@spike/room-model';
import PhotosSelector from 'components/UI/V3/PhotosSelector';
import styles from './RoomDetails.module.scss';
import { Button } from './Button';
import { Textarea } from 'components/UI/V3/Forms/Textarea/Textarea';
import Select, { SelectOption } from 'components/UI/V3/Forms/Select';

const DAYCARE = 'daycare';
const BOARDING = 'boarding';

const roomTypeOptions: Array<SelectOption<never>> = [
    { value: DAYCARE, label: 'Daycare' },
    { value: BOARDING, label: 'Boarding' }
];

const renderRoomTypeValues = (options: Array<SelectOption<never>>) =>
    options.length === roomTypeOptions.length ? 'Boarding & Daycare' : options[0].label;

interface RoomDetailsProps {
    room: Room;
    nameError: string | null;
    className?: string;
    onChange?: (room: Room) => void;
    onNext?: () => void;
}

export const RoomDetails: FunctionComponent<RoomDetailsProps> = props => {
    const dispatch = useDispatch();

    const staffMembers = useSelector<RootState, Array<Staff>>(state => state.staff.staff);
    const staffStatus = useSelector<RootState, StaffStatus>(state => state.staff.status);

    const [loading, setLoading] = useState({ staff: true });

    useEffect(() => {
        if (staffMembers.length === 0) {
            dispatch(fetchStaffThunk());
        } else {
            setLoading(prev => ({ ...prev, staff: false }));
        }
    }, []);

    useNonInitialEffect(() => {
        switch (staffStatus) {
            case StaffStatus.FetchSuccess:
                setLoading(prev => ({ ...prev, staff: false }));
                break;
            case StaffStatus.Error:
                setLoading(prev => ({ ...prev, staff: false }));
                break;
        }
    }, [staffStatus]);

    const disableNext = useMemo(
        () => isEmpty(props.room.name) || (!props.room.daycareEnabled && !props.room.boardingEnabled),
        [props.room]
    );

    const changeNameHandler = (event: ChangeEvent<HTMLInputElement>) => {
        props.onChange?.({ ...props.room, name: event.target.value });
    };

    const selectedRoomTypes = useMemo(
        () => [...(props.room.daycareEnabled ? [DAYCARE] : []), ...(props.room.boardingEnabled ? [BOARDING] : [])],
        [props.room.daycareEnabled, props.room.boardingEnabled]
    );

    const changeRoomTypeHandler = (values: Array<string>) => {
        props.onChange?.({
            ...props.room,
            daycareEnabled: values.includes(DAYCARE),
            boardingEnabled: values.includes(BOARDING)
        });
    };

    const changeDescriptionHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
        props.onChange?.({ ...props.room, description: event.target.value });
    };

    const selectStaffHandler = (selectedStaffId: number) => {
        props.onChange?.({
            ...props.room,
            staffIds: [...props.room.staffIds.filter(staffId => staffId !== selectedStaffId), selectedStaffId]
        });
    };

    const selectAllStaffHandler = (staffIds: Array<number>) => {
        props.onChange?.({ ...props.room, staffIds: [...staffIds] });
    };

    const removeStaffHandler = (removedStaffId: number) => {
        props.onChange?.({
            ...props.room,
            staffIds: props.room.staffIds.filter(staffId => staffId !== removedStaffId)
        });
    };

    const tagOptions = useMemo(
        () =>
            staffMembers
                .filter(member => member.active && !member.deleted)
                .map(member => ({
                    id: member.id ?? 0,
                    name: `${member.person.firstName} ${member.person.lastName}`,
                    image: member.person.avatar
                })),
        [staffMembers]
    );

    const changePhotosHandler = (photos: Array<RoomPhoto>) => {
        props.onChange?.({ ...props.room, photos });
    };

    return (
        <div className={styles.container}>
            <div className={styles.row}>
                <Input
                    label="Room Name"
                    required={true}
                    value={props.room.name}
                    error={props.nameError}
                    onChange={changeNameHandler}
                />
                <Select
                    label="Room Type"
                    required={true}
                    multiple={true}
                    options={roomTypeOptions}
                    values={selectedRoomTypes}
                    placeholder="Select Room Type"
                    renderValues={renderRoomTypeValues}
                    onChangeMultiple={changeRoomTypeHandler}
                />
            </div>
            <Textarea
                label="Write a Short Description"
                value={props.room.description}
                onChange={changeDescriptionHandler}
                maxLength={400}
            />
            <TagsSelector
                title="Assign Staff Members"
                tags={tagOptions}
                selected={staffMembers
                    .filter(member => props.room.staffIds.includes(member.id ?? 0))
                    .map(member => ({
                        id: member.id ?? 0,
                        name: `${member.person.firstName} ${member.person.lastName}`,
                        image: member.person.avatar
                    }))}
                selectAllLabel="All Members"
                selectAllIcon={faUsers}
                loading={loading.staff}
                onSelect={selectStaffHandler}
                onSelectAll={selectAllStaffHandler}
                onRemove={removeStaffHandler}
            />
            <PhotosSelector
                title="Photos"
                photos={props.room.photos}
                showCoverTag={true}
                maxPhotos={10}
                multipleSelection={true}
                onChange={changePhotosHandler}
            />
            <Button
                label="Next"
                disabled={disableNext}
                onClick={props.onNext}
            />
        </div>
    );
};

export default RoomDetails;
